import type {LatestPeakLoadWindow} from '@domain/electricity'
import type {MaterialDemandByDay} from '@domain/material-demand'
import {StockLevelOverwrite, isStorageWithMaterialGuard} from '@domain/material-storage'
import {MaterialWithRecipes} from '@domain/materials'
import type {UpmPlant} from '@hconnect/upm'
import moment from 'moment-timezone'

import {AutofillStatus} from '../../../shared/enums'
import type {
  Schedule,
  Comment,
  ElectricityResponse,
  ScheduleCostAvoidance,
  CostAvoidanceMonthly,
  CostAvoidanceForRange,
  MaterialOrder,
  ScheduleAgreementsByVendor,
  StockDevelopmentResponse,
  PlantConfigData,
  ElectricityPrice,
  GroupedUnplannedDelivery,
  AssetCapacityResponse,
  StockReachResponse,
  OptimizedSchedule,
  OptimizerRunResponse,
  OptimizerConstraintsDTO
} from '../../../shared/interfaces/api'
import {OptimizerSettingsDTO} from '../../../shared/interfaces/api/optimizer'
import {
  getAssetsFromHistoryByDate,
  getBaseLoadPowerConsumptionFromHistoryAssets,
  getHistoryAssetsById,
  mapScheduleItemIdToAssetData,
  sortAssets
} from '../../../shared/selectors/assets'
import {
  getStoragesFromHistory,
  isHistoryStorageWithMaterialGuard
} from '../../../shared/selectors/storages'
import {MockState} from '../../mockState'

import assetsCapacityByMonth from './assetsCapacityByMonth.json'
import comments from './comments.json'
import constraints from './constraints.json'
import costAvoidanceDaily from './costAvoidanceDaily.json'
import costAvoidanceMonthly from './costAvoidanceMonthly.json'
import costAvoidancesForRange from './costAvoidancesForRange.json'
import currentMaterialStorages from './currentStorages'
import electricity from './electricity.json'
import electricityPrice from './electricityPrice.json'
import historyAssets from './historyAssets'
import materialStorageHistory from './historyStorages'
import materialDemandByDayCombined from './materialDemandByDayCombined.json'
import materialOrders from './materialOrders.json'
import {materialsHistory} from './materialsHistory'
import materialsWithRecipes from './materialsWithRecipes.json'
import materialUnplannedDeliveries from './materialUnplannedDeliveries.json'
import optimizedSchedules from './optimizedSchedules.json'
import optimizerRuns from './optimizerRuns.json'
import optimizerSettings from './optimizerSettings.json'
import peakLoadWindows from './peakLoadWindows.json'
import plantCofig from './plantConfig.json'
import schedule from './schedule.json'
import scheduleAgreements from './scheduleAgreementsByVendor.json'
import schedulesCostAvoidance from './schedulesCostAvoidance.json'
import stockDevelopment from './stockDevelopment.json'
import stockReach from './stockReach.json'
import upmPlants from './upmPlants.json'

const startOfPlan = '2019-08-15T22:00:00.000Z'
const endOfPlan = '2019-08-22T22:00:00.000Z'

export const mockState: MockState = {
  burglengenfeld: {
    start: startOfPlan,
    end: endOfPlan,
    peakLoadWindows: peakLoadWindows as LatestPeakLoadWindow[],
    electricityPrice: electricityPrice as ElectricityPrice,
    plantConfig: plantCofig as PlantConfigData,
    schedule: schedule as Schedule,
    autofillStatus: {
      data: {
        status: AutofillStatus.Unknown,
        last_updated_at: null
      },
      inProgressChange: undefined
    },
    schedulesCostAvoidance: schedulesCostAvoidance as Record<string, ScheduleCostAvoidance>,
    costAvoidanceMontly: costAvoidanceMonthly as CostAvoidanceMonthly[],
    costAvoidanceDaily: costAvoidanceDaily as ScheduleCostAvoidance[],
    costAvoidancesForRange: costAvoidancesForRange as Record<string, CostAvoidanceForRange>,
    optimizedSchedules: optimizedSchedules as unknown as OptimizedSchedule[],
    electricity: electricity as ElectricityResponse,
    comments: comments as Comment[],
    stockReach: stockReach as StockReachResponse,
    materialsWithRecipes: materialsWithRecipes as unknown as MaterialWithRecipes[],
    recipesById: (materialsWithRecipes as unknown as MaterialWithRecipes[])
      .flatMap((material) => material.recipes)
      .reduce((recipesById, recipe) => ({...recipesById, [recipe.id]: recipe}), {}),
    materialsById: (materialsWithRecipes as unknown as MaterialWithRecipes[]).reduce(
      (materialsById, material) => ({...materialsById, [material.id]: material}),
      {}
    ),
    materialsHistory,
    historyAssets,
    stockOverwrites: {} as Record<string, StockLevelOverwrite[]>,
    assets: getAssetsFromHistoryByDate(historyAssets, moment.utc(endOfPlan)),
    sortedAssets: sortAssets(getAssetsFromHistoryByDate(historyAssets, moment.utc(endOfPlan))),
    assetsById: getHistoryAssetsById(historyAssets, moment.utc(endOfPlan)),
    baseLoad: getBaseLoadPowerConsumptionFromHistoryAssets(historyAssets, moment.utc(endOfPlan)),
    currentMaterialStorages: currentMaterialStorages,
    currentMaterialStoragesWithMaterial: currentMaterialStorages.filter(isStorageWithMaterialGuard),
    materialStorageHistory,
    historyMaterialStorage: getStoragesFromHistory(materialStorageHistory, moment.utc(endOfPlan)),
    historyStoragesWithMaterial: getStoragesFromHistory(
      materialStorageHistory,
      moment.utc(endOfPlan)
    ).filter(isHistoryStorageWithMaterialGuard),
    materialOrders: materialOrders as MaterialOrder[],
    scheduleAgreements: scheduleAgreements as ScheduleAgreementsByVendor[],
    materialUnplannedDeliveries: materialUnplannedDeliveries as GroupedUnplannedDelivery[],
    stockDevelopment: stockDevelopment as StockDevelopmentResponse,
    optimizedStockDevelopment: Object.fromEntries(
      Object.entries(stockDevelopment).map(([materialId, stockDevelopmentEntry]) => [
        materialId,
        {
          actual: stockDevelopmentEntry.actual,
          forecast: stockDevelopmentEntry.forecast.map(({datetime, value}) => ({
            datetime,
            // increasing value by 5% for testing purposes
            value: value * 1.05
          }))
        }
      ])
    ) as StockDevelopmentResponse,
    assetHistoryDataByScheduleItemId: mapScheduleItemIdToAssetData(
      schedule.schedules as Schedule['schedules'],
      historyAssets
    ),
    assetsCapacityByMonth: assetsCapacityByMonth as Record<string, AssetCapacityResponse>,
    materialDemandByDay: materialDemandByDayCombined as MaterialDemandByDay,
    optimizerSettings: optimizerSettings as OptimizerSettingsDTO,
    backendErrors: {},
    optimizerRuns: optimizerRuns as OptimizerRunResponse[],
    optimizerConstraints: constraints as OptimizerConstraintsDTO,
    upm: {
      plants: upmPlants as UpmPlant[]
    }
  }
}
